/**
 * Máscaras de digitação (ADR-055): o campo mostra o valor como se escreve no Brasil enquanto a
 * pessoa digita. Só formatam o texto — quem converte para a API continua sendo `lib/schemas.ts`
 * (que já lê "1.000,00", "6,5", "25,5"), e quem valida é o servidor.
 */

const digits = (value: string) => value.replace(/\D/g, '');

const group = (integer: string) => integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/**
 * Dinheiro preenchido da direita, como em app de banco: cada dígito entra nos centavos —
 * 1 → 0,01; 100 → 1,00; 100000 → 1.000,00. Sempre duas casas, sem letra nem sinal.
 */
export function maskMoney(value: string): string {
  const only = digits(value)
    .replace(/^0+(?=\d)/, '')
    .slice(0, 12);
  if (only === '') return '';
  const padded = only.padStart(3, '0');
  return `${group(padded.slice(0, -2))},${padded.slice(-2)}`;
}

/** Valor da API ("1500.00", "2.4") no campo mascarado ("1.500,00", "2,40"). */
export function moneyToMasked(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const [integer = '0', fraction = ''] = value.split('.');
  return maskMoney(`${integer}${fraction.padEnd(2, '0').slice(0, 2)}`);
}

/**
 * Decimal digitado com vírgula (percentual, medida, preço com mais casas): só dígitos e uma
 * vírgula, com limite de casas; o inteiro ganha o ponto de milhar quando `grouped`.
 */
export function maskDecimal(
  value: string,
  {
    integer = 6,
    fraction = 2,
    grouped = false,
  }: { integer?: number; fraction?: number; grouped?: boolean } = {},
): string {
  // Com milhar, o ponto é do milhar; sem, quem digita "25.5" quer dizer 25,5.
  const normalized = (grouped ? value.replace(/\./g, '') : value.replace(/\./g, ',')).replace(
    /[^\d,]/g,
    '',
  );
  const [whole = '', ...rest] = normalized.split(',');
  const head = whole.slice(0, integer);
  const shown = grouped ? group(head) : head;
  if (rest.length === 0 || fraction === 0) return shown;
  return `${shown || '0'},${rest.join('').slice(0, fraction)}`;
}

/** Inteiro: só dígitos, até `length`. */
export function maskInteger(value: string, length = 9): string {
  return digits(value).slice(0, length);
}

/** CNPJ numérico ou alfanumérico (letras nas 12 primeiras posições): 00.000.000/0000-00. */
export function maskCnpj(value: string): string {
  const raw = value.toUpperCase().replace(/[^0-9A-Z]/g, '');
  const base = raw.slice(0, 12);
  const check = raw.slice(12).replace(/\D/g, '').slice(0, 2);
  const all = `${base}${check}`;
  const parts = [
    all.slice(0, 2),
    all.slice(2, 5),
    all.slice(5, 8),
    all.slice(8, 12),
    all.slice(12, 14),
  ];
  let result = parts[0]!;
  if (parts[1]) result += `.${parts[1]}`;
  if (parts[2]) result += `.${parts[2]}`;
  if (parts[3]) result += `/${parts[3]}`;
  if (parts[4]) result += `-${parts[4]}`;
  return result;
}

/** CPF: 000.000.000-00. */
export function maskCpf(value: string): string {
  const only = digits(value).slice(0, 11);
  return only
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

/** Telefone com DDD: (11) 1234-5678 ou (11) 91234-5678. */
export function maskPhone(value: string): string {
  const only = digits(value).slice(0, 11);
  if (only.length <= 2) return only.length ? `(${only}` : '';
  const ddd = only.slice(0, 2);
  const rest = only.slice(2);
  const split = rest.length > 8 ? 5 : 4;
  return rest.length > split
    ? `(${ddd}) ${rest.slice(0, split)}-${rest.slice(split)}`
    : `(${ddd}) ${rest}`;
}

/** CEP: 00000-000. */
export function maskCep(value: string): string {
  const only = digits(value).slice(0, 8);
  return only.length > 5 ? `${only.slice(0, 5)}-${only.slice(5)}` : only;
}
